<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-3xl">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold" v-if="updating === true">
                                                Update Office `{{ form.name }}`
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white font-bold" v-else>
                                                New Office
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white" v-if="updating === false">
                                                Get started by filling in the information below to create a new
                                                office.
                                            </p>
                                            <p class="text-sm text-white" v-else>
                                                Make any changes to the user by updating the information below.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="pt-6 pb-5 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Office
                                                        Information</h3>
                                                </div>
                                                <div>
                                                    <label for="name"
                                                           class="block text-sm text-gray-900">
                                                        Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="name" v-model="form.name"
                                                               placeholder="Enter the office name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.name}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="short_code"
                                                           class="block text-sm text-gray-900">
                                                        Short Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.short_name" id="short_code"
                                                               placeholder="Enter a short name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.short_name}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="unit4_code"
                                                           class="block text-sm text-gray-900">
                                                        Unit4 Company Code
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.unit4_code" id="unit4_code"
                                                               placeholder="e.g. 1012GB0000"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.unit4_code}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="short_code"
                                                           class="block text-sm text-gray-900">
                                                        Registered Legal Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.registered_legal_name"
                                                               id="registered_legal_name"
                                                               placeholder="Enter the registered legal name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.registered_legal_name}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="role"
                                                           class="block text-sm text-gray-900">
                                                        Office Manager
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="role" name="role" v-model="form.office_manager_id"
                                                                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-light-pitch-purple sm:text-sm rounded-md">
                                                            <option disabled value="">Please select an office manager
                                                            </option>
                                                            <option v-for="user in users" :value="user.id"
                                                                    v-html="user.label ?? (user.first_name + ' ' + user.last_name)"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="email"
                                                           class="block text-sm text-gray-900">
                                                        Email
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.email" id="email"
                                                               placeholder="mail@example.com"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.email}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="telephone"
                                                           class="block text-sm text-gray-900">
                                                        Telephone
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.telephone" id="telephone"
                                                               placeholder="+44123456789"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.telephone}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="mobile"
                                                           class="block text-sm text-gray-900">
                                                        Mobile
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.mobile" id="mobile"
                                                               placeholder="+44712345678"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.mobile}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="website"
                                                           class="block text-sm text-gray-900">
                                                        Website
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.website" id="website"
                                                               placeholder="www.example.com"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.website}"
                                                        />

                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="role"
                                                           class="block text-sm text-gray-900">
                                                        Currency
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="role" name="role" v-model="form.currency_id"
                                                                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-light-pitch-purple sm:text-sm rounded-md">
                                                            <option disabled value="">Select a currency</option>
                                                            <option v-for="currency in currencies" :value="currency.id"
                                                                    v-html="currency.name"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="role"
                                                           class="block text-sm text-gray-900">
                                                        Commission Country
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="role" name="role" v-model="form.commission_country_id"
                                                                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-light-pitch-purple sm:text-sm rounded-md">
                                                            <option disabled value="">Please select a country</option>
                                                            <option v-for="country in countries" :value="country.id"
                                                                    v-html="country.name"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="pt-6 pb-5 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Address
                                                        Information</h3>
                                                </div>
                                                <div>
                                                    <label for="street"
                                                           class="block text-sm text-gray-900">
                                                        Street Number / Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="street" v-model="form.address.street"
                                                               placeholder="Enter a street number/name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors['address.street']}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="address_line_1"
                                                           class="block text-sm text-gray-900">
                                                        Address Line 1
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="address_line_1"
                                                               v-model="form.address.address_line_1"
                                                               placeholder="Enter address line 1"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors['address.address_line_1']}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="address_line_2"
                                                           class="block text-sm text-gray-900">
                                                        Address Line 2
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="address_line_2"
                                                               v-model="form.address.address_line_2"
                                                               placeholder="Enter address line 2"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors['address.address_line_2']}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="name"
                                                           class="block text-sm text-gray-900">
                                                        Address Line 3
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="name"
                                                               v-model="form.address.address_line_3"
                                                               placeholder="Enter address line 3"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors['address.address_line_3']}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="name"
                                                           class="block text-sm text-gray-900">
                                                        City
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="name" v-model="form.address.city"
                                                               placeholder="Enter a city"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors['address.name']}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="name"
                                                           class="block text-sm text-gray-900">
                                                        Code
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="name" v-model="form.address.code"
                                                               placeholder="Enter a postal / zip code"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors['address.cod']}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="role"
                                                           class="block text-sm text-gray-900">
                                                        Country
                                                    </label>
                                                    <div class="mt-1">
                                                        <select id="role" name="role" v-model="form.address.country_id"
                                                                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-light-pitch-purple sm:text-sm rounded-md">
                                                            <option disabled value="">Please select a country</option>
                                                            <option v-for="country in countries" :value="country.id"
                                                                    v-html="country.name"></option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="pt-6 pb-5 grid grid-cols-2 gap-4">
                                                <div class="col-span-2">
                                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Tax
                                                        Information</h3>
                                                </div>
                                                <div>
                                                    <label for="name"
                                                           class="block text-sm text-gray-900">
                                                        Company Registration Number
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="name"
                                                               v-model="form.company_registration_number"
                                                               placeholder="Enter a company registration number"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.company_registration_number}"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="address_line_1"
                                                           class="block text-sm text-gray-900">
                                                        VAT Number
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="address_line_1" v-model="form.vat_number"
                                                               placeholder="Enter a vat number"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.vat_number}"
                                                        />
                                                    </div>
                                                </div>

                                                <div class="flex grid grid-cols-2">
                                                    <div class="flex">
                                                        <div class="flex items-center h-5">
                                                            <input name="allow_toms_vat"
                                                                   v-model="form.allow_toms_vat" value="1"
                                                                   type="checkbox"
                                                                   class="focus:ring-amaze-coral-dark h-4 w-4 text-amaze-coral h-4 w-4 border-gray-300 rounded">
                                                        </div>
                                                        <div class="min-w-0 flex-1 text-sm ml-2">
                                                            <label for="allow_toms_vat"
                                                                   class="font-medium text-gray-700 select-none">
                                                                Allow TOMS VAT
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div class="flex">
                                                        <div class="flex items-center h-5">
                                                            <input name="has_vat_breakdown"
                                                                   v-model="form.has_vat_breakdown"
                                                                   type="checkbox"
                                                                   class="focus:ring-amaze-coral-dark h-4 w-4 text-amaze-coral border-gray-300 rounded">
                                                        </div>
                                                        <div class="min-w-0 flex-1 text-sm ml-2">
                                                            <label for="allow_toms_vat"
                                                                   class="font-medium text-gray-700 select-none">
                                                                Display VAT Breakdown
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, ExclamationCircleIcon} from '@heroicons/vue/solid'
import {useForm} from '@inertiajs/vue3'
import Inertia from '@inertiajs/vue3'
import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import {mapState} from "vuex";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        ExclamationCircleIcon,
        XIcon,
    },
    setup(prop, context) {

        const show = ref(false)

        const showErrors = ref(false)

        const updating = false;

        const form = useForm({
            id: null,
            name: null,
            short_name: null,
            unit4_code: null,
            registered_legal_name: null,
            office_manager_id: '',
            email: null,
            telephone: null,
            mobile: null,
            website: null,
            currency_id: '',
            commission_country_id: '',
            address: {
                street: null,
                address_line_1: null,
                address_line_2: null,
                address_line_3: null,
                city: null,
                code: null,
                country_id: ""
            },
            vat_number: null,
            company_registration_number: null,
            allow_toms_vat: false,
            has_vat_breakdown: false
        })

        function submit() {
            if (this.updating) {
                this.form.post(route('admin.offices.office.update', this.form.id), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            } else {
                this.form.post(route('admin.offices.store'), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            }
        }

        function close() {
            this.show = false;
            this.form.reset();
            this.form.clearErrors();
        }

        async function open() {
            await this.$store.dispatch('offices/getData', {
                currentUserId: this.form.office_manager_id,
            })
            this.form.clearErrors();
            this.show = true;
        }

        return {
            show,
            form,
            submit,
            updating,
            close,
            open,
            showErrors
        }
    },
    computed: {
        ...mapState({
            users: state => state.offices.users ?? null,
            currencies: state => state.offices.currencies ?? null,
            countries: state => state.offices.countries ?? null
        }),
    }
}
</script>
